import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { post } from '../lib/apiClient';

export interface OAuthConnectResult {
  provider: Record<string, unknown>;
  sender_profile_id: string | null;
}

/**
 * Returns the redirect URI for Google OAuth (always the React callback page).
 */
function getCallbackUri(): string {
  return `${window.location.origin}/oauth/email/callback`;
}

/**
 * Returns the redirect URI for Microsoft OAuth.
 * Uses VITE_MICROSOFT_REDIRECT_URI if set (must match Azure AD app registration),
 * otherwise falls back to the standard callback page.
 */
function getMicrosoftCallbackUri(): string {
  return (import.meta.env.VITE_MICROSOFT_REDIRECT_URI as string) || getCallbackUri();
}

/**
 * Builds the Google OAuth authorization URL for mailbox access.
 */
function buildGoogleAuthUrl(redirectUri: string, state: string): string {
  const params = new URLSearchParams({
    client_id:     import.meta.env.VITE_GOOGLE_CLIENT_ID as string,
    redirect_uri:  redirectUri,
    response_type: 'code',
    scope:         'https://mail.google.com/ email https://www.googleapis.com/auth/gmail.readonly',
    access_type:   'offline',
    prompt:        'consent',
    state,
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}

/**
 * Builds the Microsoft OAuth authorization URL for mailbox access.
 * Includes PKCE code_challenge — required for Azure AD SPA-registered redirect URIs.
 */
function buildMicrosoftAuthUrl(redirectUri: string, state: string, codeChallenge: string): string {
  const tenantId = (import.meta.env.VITE_MICROSOFT_TENANT_ID as string) ?? 'common';
  const params = new URLSearchParams({
    client_id:             import.meta.env.VITE_MICROSOFT_CLIENT_ID as string,
    redirect_uri:          redirectUri,
    response_type:         'code',
    scope:                 'openid email https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/Mail.ReadWrite offline_access',
    state,
    code_challenge:        codeChallenge,
    code_challenge_method: 'S256',
  });
  return `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params}`;
}

/**
 * Generates a PKCE code_verifier and code_challenge (S256).
 * Uses the Web Crypto API — available in all modern browsers over HTTPS.
 */
async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const array    = crypto.getRandomValues(new Uint8Array(32));
  const verifier = btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

  const encoded      = new TextEncoder().encode(verifier);
  const hashBuffer   = await crypto.subtle.digest('SHA-256', encoded);
  const challenge    = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

  return { verifier, challenge };
}

/** Sentinel error thrown when the user closes the popup before completing OAuth. */
export class OAuthPopupClosedError extends Error {
  constructor() { super('popup_closed'); }
}

/**
 * Returns a Promise that resolves when we receive the OAUTH_EMAIL_CALLBACK
 * postMessage from the /oauth/email/callback React page (same origin).
 * Rejects immediately if the popup is closed before the message arrives,
 * or after 5 minutes (safety timeout).
 */
function waitForOAuthMessage(
  expectedState: string,
  popup: Window,
): Promise<{ code: string; error: string }> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('OAuth timed out. Please try again.'));
    }, 5 * 60 * 1000);

    // Poll every 500 ms — if the popup is closed before sending a message,
    // reject immediately so the button re-enables without waiting 5 minutes.
    const closedPoll = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new OAuthPopupClosedError());
      }
    }, 500);

    function handler(event: MessageEvent) {
      // Only accept same-origin messages (the React callback page)
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.type !== 'OAUTH_EMAIL_CALLBACK') return;
      if (event.data.state !== expectedState) return;

      cleanup();
      resolve({ code: event.data.code ?? '', error: event.data.error ?? '' });
    }

    function cleanup() {
      clearTimeout(timeout);
      clearInterval(closedPoll);
      window.removeEventListener('message', handler);
    }

    window.addEventListener('message', handler);
  });
}

/**
 * Hook for connecting a Google mailbox via OAuth.
 */
export function useConnectGoogleMailbox() {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);

  async function connect(): Promise<OAuthConnectResult> {
    const state       = crypto.randomUUID();
    const callbackUri = getCallbackUri();

    // IMPORTANT: window.open must be called synchronously (before any await)
    // to avoid popup blockers.
    const authUrl = buildGoogleAuthUrl(callbackUri, state);
    const popup   = window.open(authUrl, 'oauth_google_email', 'width=520,height=650,left=200,top=100');

    if (!popup) {
      throw new Error('Popup blocked. Please allow popups for this site and try again.');
    }

    setIsPending(true);
    try {
      const { code, error } = await waitForOAuthMessage(state, popup);

      if (error) {
        throw new Error(`Google OAuth error: ${error}`);
      }
      if (!code) {
        throw new Error('No authorization code received from Google.');
      }

      const response = await post<OAuthConnectResult>('settings/email-providers/oauth/google/connect', {
        code,
        redirect_uri: callbackUri,
        tc_accepted:  true,
      });

      queryClient.invalidateQueries({ queryKey: ['email-providers'] });
      queryClient.invalidateQueries({ queryKey: ['sender-identities'] });

      return response.data;
    } finally {
      setIsPending(false);
    }
  }

  return { connect, isPending };
}

/**
 * Hook for connecting a Microsoft mailbox via OAuth.
 *
 * Uses PKCE (code_challenge / code_verifier) because the Azure AD redirect URI
 * is registered as a Single-Page Application, which requires PKCE for all
 * authorization-code flows (including those with a client_secret).
 *
 * The popup is opened synchronously with about:blank to avoid popup blockers,
 * then navigated to the real auth URL after the async PKCE generation.
 */
export function useConnectMicrosoftMailbox() {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState(false);

  async function connect(): Promise<OAuthConnectResult> {
    const state       = crypto.randomUUID();
    const callbackUri = getMicrosoftCallbackUri();

    // IMPORTANT: Open the popup synchronously (before any await) to satisfy
    // browser popup-blocker heuristics that require a user-gesture call chain.
    // We navigate it to the real URL after PKCE is ready.
    const popup = window.open('about:blank', 'oauth_microsoft_email', 'width=520,height=650,left=200,top=100');

    if (!popup) {
      throw new Error('Popup blocked. Please allow popups for this site and try again.');
    }

    setIsPending(true);
    try {
      // Generate PKCE — async but popup is already open.
      const { verifier, challenge } = await generatePKCE();

      // Navigate the popup to the real auth URL now that we have the challenge.
      popup.location.href = buildMicrosoftAuthUrl(callbackUri, state, challenge);

      const { code, error } = await waitForOAuthMessage(state, popup);

      if (error) {
        throw new Error(`Microsoft OAuth error: ${error}`);
      }
      if (!code) {
        throw new Error('No authorization code received from Microsoft.');
      }

      const response = await post<OAuthConnectResult>('settings/email-providers/oauth/microsoft/connect', {
        code,
        redirect_uri:   callbackUri,
        code_verifier:  verifier,
        tc_accepted:    true,
      });

      queryClient.invalidateQueries({ queryKey: ['email-providers'] });
      queryClient.invalidateQueries({ queryKey: ['sender-identities'] });

      return response.data;
    } finally {
      setIsPending(false);
    }
  }

  return { connect, isPending };
}
